import { useEffect, useState } from 'react';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { loadEntryRecipeRelevance, type EntryRecipeRelevance } from '../../services/recipeRelevanceService';

/** Read cached profiles only. Newly classified responses can reorder while their shelf is open. */
export function useRecipeRelevance(campaignId: string, responseId: string | undefined, orgId: string | undefined, recipeIds: string[]) {
  const { supabase, user } = useSupabase();
  const ids = recipeIds.join(',');
  const scope = JSON.stringify([user?.id, orgId, campaignId, responseId, ids]);
  const [snapshot, setSnapshot] = useState<{ scope: string; context: EntryRecipeRelevance }>();
  useEffect(() => {
    if (!responseId || !ids || !supabase) return undefined;
    let active = true;
    let reported = false;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        if (!document.hidden) {
          const context = await loadEntryRecipeRelevance(supabase, campaignId, responseId, ids.split(','));
          if (active) setSnapshot({ scope, context });
          reported = false;
        }
      } catch (error) {
        if (active && !reported) {
          reported = true;
          captureException(error instanceof Error ? error : new Error(String(error)), 'RecipeRelevance.load', { sessionId: campaignId, responseId });
        }
      } finally {
        if (active) timer = setTimeout(refresh, 60_000);
      }
    };
    void refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [campaignId, responseId, ids, scope, supabase]);
  return snapshot?.scope === scope ? snapshot.context : undefined;
}
